import { useEffect, useState, useCallback } from 'react';
import {
  createPersonAttribute,
  deletePersonAttribute,
  getAttributeTypes,
  getPersonAttributes,
  updatePersonAttribute,
} from '../services/person-attributes.service';

/**
 * @typedef {Object} AttributeTypeOption
 * @property {number} id
 * @property {string} name
 */

/**
 * @typedef {Object} PersonAttributeRow
 * @property {number} id
 * @property {string} attributeName
 * @property {string} value
 * @property {number|null|undefined} [locationKey]
 */

export const useAttributeModalHook = ({ open, person, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState(/** @type {AttributeTypeOption[]} */ ([]));
  const [rows, setRows] = useState(/** @type {PersonAttributeRow[]} */ ([]));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [editingId, setEditingId] = useState(/** @type {number | null} */ (null));
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(/** @type {number | null} */ (null));
  const [adding, setAdding] = useState(false);
  const [newTypeId, setNewTypeId] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newLocation, setNewLocation] = useState('');

  const personId = person?.personId;

  const reload = useCallback(async () => {
    if (!personId) return;
    setLoading(true);
    setError(null);
    try {
      const [attributeTypes, attrs] = await Promise.all([
        getAttributeTypes(),
        getPersonAttributes(personId),
      ]);
      setTypes(Array.isArray(attributeTypes) ? attributeTypes : []);
      setRows(Array.isArray(attrs) ? attrs : []);
    } catch (error) {
      console.error('PersonsManager: failed to load attribute types or person attributes', error);
      setError('Failed to load data');
      setRows([]);
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    if (open && personId) {
      setEditingId(null);
      setAdding(false);
      setDeleteId(null);
      setNewTypeId('');
      setNewValue('');
      setNewLocation('');
      reload();
    }
  }, [open, personId, reload]);

  const handleSaveEdit = async (row) => {
    if (!personId) return;
    setSaving(true);
    try {
      await updatePersonAttribute(personId, row.id, { value: editValue });
      setEditingId(null);
      await reload();
      onSaved?.();
    } catch (error) {
      console.error('PersonsManager: failed to update person attribute', error);
      setError('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!personId || deleteId === null || deleteId === undefined) return;
    setSaving(true);
    try {
      await deletePersonAttribute(personId, deleteId);
      setDeleteId(null);
      await reload();
      onSaved?.();
    } catch (error) {
      console.error('PersonsManager: failed to delete person attribute', error);
      setError('Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!personId || !newTypeId || !newValue.trim()) return;
    setSaving(true);
    try {
      const body = {
        person_attribute_key: Number(newTypeId),
        value: newValue.trim(),
      };
      if (newLocation.trim() !== '') {
        const locationKey = Number.parseInt(newLocation, 10);
        if (!Number.isNaN(locationKey)) body.location_key = locationKey;
      }
      await createPersonAttribute(personId, body);
      setAdding(false);
      setNewTypeId('');
      setNewValue('');
      setNewLocation('');
      await reload();
      onSaved?.();
    } catch (error) {
      console.error('PersonsManager: failed to create person attribute', error);
      setError('Create failed');
    } finally {
      setSaving(false);
    }
  };

  const startEditRow = (row) => {
    setEditingId(row.id);
    setEditValue(row.value);
  };

  return {
    loading,
    types,
    rows,
    error,
    editingId,
    editValue,
    setEditValue,
    saving,
    deleteId,
    setDeleteId,
    adding,
    setAdding,
    newTypeId,
    setNewTypeId,
    newValue,
    setNewValue,
    newLocation,
    setNewLocation,
    handleSaveEdit,
    handleDelete,
    handleAdd,
    startEditRow,
    cancelEdit: () => setEditingId(null),
  };
};
